import Vue from 'vue';
import type { ActionTree, GetterTree, MutationTree } from 'vuex';
import type { DealsState } from '~/types/store/deals';
import { votableTypes } from '~/components/partials/war-room/constants';
import { keyBy } from '~/components/utils/core/array';
import { sortByDateField } from '~/components/utils/sorting_func';
import { withRollback } from '~/components/utils/store';
import { DEAL_SOURCE_WIN_WIRE } from '~/constants/deals';
import type { RootState } from '~/types/store/index';
import type { IDeal } from '~/types/community';
import type { GetDealsResponse } from '~/types/api';

const PER_PAGE = 10;
const sortByCreatedAt = sortByDateField('created_at');

const getInitialState = (): DealsState => ({
  deals: {},
  dealsSummary: {
    totalAmount: 0,
    totalCount: 0,
  },
  dealsPagination: {
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
  },
});

export const state = getInitialState;

export const getters: GetterTree<DealsState, RootState> = {
  deals(state) {
    return Object.values(state.deals).sort(sortByCreatedAt);
  },
};

export const mutations: MutationTree<DealsState> = {
  updateState(state, newState: DealsState) {
    Object.assign(state, newState);
  },
  addDeals(state, { deals }: { deals: IDeal[] }) {
    state.deals = { ...state.deals, ...keyBy(deals, 'id') };
  },
  clapDeal(state, { dealId }) {
    const deal = state.deals[dealId];

    if (deal) {
      deal.interactions_count = deal.interactions_count || 0;
      deal.interactions_count += 1;
      deal.current_rating = true;
    }
  },
  deleteDeal(state, { dealId }) {
    Vue.delete(state.deals, dealId);
  },
};

export const actions: ActionTree<DealsState, RootState> = {
  async fetchDeals({ commit }, { page }) {
    const { deals, ...meta } = await this.$api.$get<GetDealsResponse>(`/api/portfolio/deals.json`, {
      params: {
        page,
        per_page: PER_PAGE,
      },
    });

    const dealsSummary = {
      totalAmount: meta.total_amount,
      totalCount: meta.total_count,
    };
    const dealsPagination = {
      totalPages: meta.total_pages,
      currentPage: meta.current_page,
      perPage: meta.per_page,
    };
    commit('addDeals', { deals });
    commit('updateState', { dealsSummary, dealsPagination });
  },
  async fetchProfileDeals({ commit }, username) {
    const { deals } = await this.$api.$get<GetDealsResponse>(`/api/portfolio/deals.json?username=${username}`);

    commit('updateState', { deals: {} });
    commit('addDeals', { deals });
  },
  async createDeal({ commit }, { deal }) {
    const { deal: newDeal } = await this.$api.$post('/api/portfolio/deals.json', {
      source: DEAL_SOURCE_WIN_WIRE,
      ...deal,
    });

    commit('addDeals', { deals: [newDeal] });
    return newDeal;
  },
  async updateDeal(_, { deal }) {
    const { deal: updatedDeal } = await this.$api.$put(`/api/portfolio/deals/${deal.id}.json`, { deal });

    return updatedDeal;
  },
  async upvoteDeal({ commit }, { dealId }) {
    commit('clapDeal', { dealId });

    await this.$api.$post('/api/war_room/interactions.json', {
      reactable_id: dealId,
      reactable_type: votableTypes.DEAL,
      value: 'clap',
    });
  },
  deleteDeal: withRollback(async function deleteDealAction({ commit }, { deal }) {
    commit('deleteDeal', { dealId: deal.id });

    if (deal.post) {
      commit('warRoom/deletePost', { postSlug: deal.post.slug }, { root: true });
    }
    await this.$api.$delete(`/api/portfolio/deals/${deal.id}.json`);
  }),
  async markDealAsViewed(_, { dealId }) {
    await this.$api.$post(`/api/portfolio/deals/${dealId}/view.json`);
  },
};
